import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Grid,
  InputAdornment,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  Paper,
} from '@mui/material';
import { Receipt, Add, Delete } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Property, Unit, unitApi } from '../services/api';
import { AccountingService, ChartOfAccount, CreateTransactionRequest } from '../services/accountingService';
import { getApiUrl } from '../config/api';

interface PurchaseInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  property?: Property;
  unit?: Unit;
  onTransactionCreated?: () => void;
}

interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  accountId: string;
}

const PurchaseInvoiceDialog: React.FC<PurchaseInvoiceDialogProps> = ({ 
  open, 
  onClose, 
  property, 
  unit, 
  onTransactionCreated 
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    invoiceDate: new Date(),
    invoiceNumber: '',
    vendorId: null as number | null,
    vendorName: '',
    vendorEmail: '',
    vendorPhone: '',
    vendorWhatsapp: '',
    vendorContactPerson: '',
    vendorAddress: '',
    dueDate: new Date(),
    notes: '',
    category: 'maintenance',
    paymentMethod: 'credit' as 'cash' | 'credit' | 'bank_transfer' | 'check' | 'credit_card',
  });
  
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([
    { description: '', quantity: 1, unitPrice: 0, amount: 0, accountId: '' }
  ]);
  
  const [expenseAccounts, setExpenseAccounts] = useState<ChartOfAccount[]>([]);
  const [accountsPayableAccount, setAccountsPayableAccount] = useState<ChartOfAccount | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allUnits, setAllUnits] = useState<Unit[]>([]);
  const [showCategoryTooltip, setShowCategoryTooltip] = useState(false);
  const [vendorSearchResults, setVendorSearchResults] = useState<any[]>([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [vendorSearchLoading, setVendorSearchLoading] = useState(false);

  // Fetch currencies for currency display
  const { data: currencies = [] } = useQuery({
    queryKey: ['currencies'],
    queryFn: async () => {
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      const data = await response.json();
      // Ensure we always return an array
      const currenciesArray = Array.isArray(data) ? data : (data.data || []);
      return currenciesArray;
    },
    retry: 3,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const loadExpenseAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const accounts = await AccountingService.getChartOfAccounts(property?.id);
      
      // Filter for expense accounts (EXPENSE type)
      const expenseAccountsList = accounts.filter(
        (account: ChartOfAccount) => account.account_type === 'EXPENSE'
      );
      
      // Find accounts payable account (LIABILITY type with 'payable' or '201' in code)
      const accountsPayable = accounts.find(
        (account: ChartOfAccount) => 
          account.account_type === 'LIABILITY' && 
          (account.code?.includes('201') || account.name?.toLowerCase().includes('payable'))
      );
      
      setExpenseAccounts(expenseAccountsList);
      setAccountsPayableAccount(accountsPayable || null);
      
      if (!accountsPayable) {
        console.warn('No accounts payable account found');
      }
    } catch (error) {
      console.error('Error loading expense accounts:', error);
      setError('Failed to load expense accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [property?.id]);

  // Load expense accounts and units when dialog opens
  useEffect(() => {
    if (open) {
      if (property) {
        loadExpenseAccounts();
        loadAllUnits();
        // Generate invoice number
        const invoiceNum = `PI-${Date.now().toString().slice(-6)}`;
        setFormData(prev => ({ ...prev, invoiceNumber: invoiceNum }));
      } else {
        setError('No property selected. Please try again.');
      }
    }
  }, [open, property, loadExpenseAccounts]);

  const loadAllUnits = async () => {
    try {
      const units = await unitApi.getAll();
      setAllUnits(units);
    } catch (error) {
      console.error('Error loading units:', error);
    }
  };

  // Vendor search functionality
  const searchVendors = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setVendorSearchResults([]);
      setShowVendorDropdown(false);
      return;
    }

    try {
      setVendorSearchLoading(true);
      const response = await fetch(getApiUrl(`/accounting/vendors-search?search=${encodeURIComponent(searchTerm)}&limit=10`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVendorSearchResults(data.data || []);
        setShowVendorDropdown(true);
      }
    } catch (error) {
      console.error('Error searching vendors:', error);
    } finally {
      setVendorSearchLoading(false);
    }
  };

  const selectVendor = (vendor: any) => {
    setFormData(prev => ({
      ...prev,
      vendorId: vendor.id,
      vendorName: vendor.name,
      vendorEmail: vendor.email || '',
      vendorPhone: vendor.phone || '',
      vendorWhatsapp: vendor.whatsapp || '',
      vendorContactPerson: vendor.contact_person || '',
      vendorAddress: vendor.address || '',
    }));
    setVendorSearchResults([]);
    setShowVendorDropdown(false);
  };

  const createVendor = async () => {
    if (!formData.vendorName.trim()) {
      setError('Vendor name is required');
      return;
    }

    try {
      setVendorSearchLoading(true);
      const response = await fetch(getApiUrl('/accounting/vendors'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.vendorName,
          email: formData.vendorEmail,
          phone: formData.vendorPhone,
          whatsapp: formData.vendorWhatsapp,
          contact_person: formData.vendorContactPerson,
          address: formData.vendorAddress,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        selectVendor(data.data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create vendor');
      }
    } catch (error) {
      console.error('Error creating vendor:', error);
      setError('Failed to create vendor');
    } finally {
      setVendorSearchLoading(false);
    }
  };

  const getUnitHierarchyNameById = (unitId: number): string => {
    const unit = allUnits.find(u => u.id === unitId);
    if (!unit) return 'Unknown Unit';
    
    const buildParentPath = (currentUnit: any): string => {
      if (!currentUnit.parent_unit_id) return currentUnit.name;
      const parentUnit = allUnits.find(u => u.id === currentUnit.parent_unit_id);
      if (!parentUnit) return currentUnit.name;
      return `${buildParentPath(parentUnit)} > ${currentUnit.name}`;
    };
    
    return buildParentPath(unit);
  };

  const addLineItem = () => {
    // Check if first item has expense category selected
    if (lineItems.length === 1 && !lineItems[0].accountId) {
      setShowCategoryTooltip(true);
      // Hide tooltip after 3 seconds
      setTimeout(() => setShowCategoryTooltip(false), 3000);
      return;
    }

    // Get the expense category from the last item (if any)
    const lastItemAccountId = lineItems.length > 0 ? lineItems[lineItems.length - 1].accountId : '';
    
    // Add new item with same expense category as previous item
    setLineItems([...lineItems, { 
      description: '', 
      quantity: 1, 
      unitPrice: 0, 
      amount: 0, 
      accountId: lastItemAccountId 
    }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index: number, field: keyof InvoiceLineItem, value: any) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Calculate amount when quantity or unitPrice changes
    if (field === 'quantity' || field === 'unitPrice') {
      updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].unitPrice;
    }
    
    setLineItems(updatedItems);
  };

  const getTotalAmount = () => {
    return lineItems.reduce((total, item) => total + item.amount, 0);
  };

  // Create purchase invoice mutation
  const createPurchaseInvoiceMutation = useMutation({
    mutationFn: async (transactionData: CreateTransactionRequest) => {
      // First create the accounting transaction
      const transactionResponse = await AccountingService.createTransaction(transactionData);
      
      // Then create the purchase invoice record
      const totalAmount = getTotalAmount();
      const purchaseInvoiceData = {
        vendor_id: formData.vendorId,
        vendor_name: formData.vendorName,
        vendor_email: formData.vendorEmail,
        vendor_phone: formData.vendorPhone,
        vendor_address: formData.vendorAddress,
        property_id: property!.id,
        unit_id: unit?.id || null,
        currency_id: property!.currency_id || 1,
        invoice_date: formData.invoiceDate.toISOString().split('T')[0],
        due_date: formData.dueDate.toISOString().split('T')[0],
        subtotal: totalAmount,
        tax_amount: 0,
        total_amount: totalAmount,
        category: formData.category,
        description: formData.notes || `Purchase Invoice ${formData.invoiceNumber}`,
        notes: formData.notes,
        payment_method: formData.paymentMethod,
        attachments: []
      };

      const purchaseInvoiceResponse = await fetch(getApiUrl('/accounting/purchase-invoices'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(purchaseInvoiceData),
      });

      if (!purchaseInvoiceResponse.ok) {
        throw new Error('Failed to create purchase invoice record');
      }

      const purchaseInvoiceResult = await purchaseInvoiceResponse.json();
      
      return {
        transaction: transactionResponse,
        purchaseInvoice: purchaseInvoiceResult.data
      };
    },
    onSuccess: (data) => {
      console.log('Purchase invoice created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['accounting-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-invoices'] });
      
      // Create custom event for transaction creation
      const customEvent = new CustomEvent('transactionCreated', {
        detail: { source: 'PurchaseInvoiceDialog' }
      });
      window.dispatchEvent(customEvent);
      
      if (onTransactionCreated) {
        onTransactionCreated();
      }
      
      handleClose();
    },
    onError: (error) => {
      console.error('Error creating purchase invoice:', error);
      setError('Failed to create purchase invoice. Please try again.');
    },
  });

  const handleClose = () => {
    setFormData({
      invoiceDate: new Date(),
      invoiceNumber: '',
      vendorId: null,
      vendorName: '',
      vendorEmail: '',
      vendorPhone: '',
      vendorWhatsapp: '',
      vendorContactPerson: '',
      vendorAddress: '',
      dueDate: new Date(),
      notes: '',
      category: 'maintenance',
      paymentMethod: 'credit',
    });
    setLineItems([{ description: '', quantity: 1, unitPrice: 0, amount: 0, accountId: '' }]);
    setError(null);
    setVendorSearchResults([]);
    setShowVendorDropdown(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!property || !formData.vendorName || !formData.invoiceNumber) {
      setError('Please fill in all required fields.');
      return;
    }

    // Debug: Log all line items before validation
    console.log('All line items before validation:', lineItems);

    // Validate line items
    const validLineItems = lineItems.filter((item, index) => {
      const isValid = item.description && item.accountId && item.amount > 0;
      console.log(`Line item ${index + 1} validation:`, {
        description: item.description,
        accountId: item.accountId,
        amount: item.amount,
        isValid: isValid
      });
      return isValid;
    });

    // Debug: Log valid line items after validation
    console.log('Valid line items after validation:', validLineItems);

    if (validLineItems.length === 0) {
      setError('Please add at least one valid line item.');
      return;
    }

    if (!accountsPayableAccount) {
      setError('Accounts Payable account not found. Please contact your administrator.');
      return;
    }

    try {
      setError(null);
      
      const totalAmount = getTotalAmount();
      console.log('Total amount calculated:', totalAmount);
      
      // Create journal entries for double-entry bookkeeping
      const journalEntries = [];
      
      // Debit entries for each expense line item
      validLineItems.forEach((item, index) => {
        console.log(`Processing line item ${index + 1}:`, item);
        journalEntries.push({
          account_id: parseInt(item.accountId),
          debit_amount: item.amount,
          credit_amount: 0,
          description: item.description
        });
      });
      
      // Credit entry for accounts payable
      journalEntries.push({
        account_id: accountsPayableAccount.id,
        debit_amount: 0,
        credit_amount: totalAmount,
        description: `Purchase Invoice ${formData.invoiceNumber} - ${formData.vendorName}`
      });

      console.log('Final journal entries:', journalEntries);

      const transactionData: CreateTransactionRequest = {
        transaction_date: formData.invoiceDate.toISOString().split('T')[0],
        description: `Purchase Invoice ${formData.invoiceNumber} - ${formData.vendorName}`,
        transaction_type: 'EXPENSE',
        property_id: property.id,
        unit_id: unit?.id,
        unit_number: unit?.unit_number,
        unit_name: unit?.name,
        currency_id: property.currency_id || 1,
        journal_entries: journalEntries,
        auto_post: false // Keep as draft
      };

      console.log('Creating purchase invoice transaction:', transactionData);
      await createPurchaseInvoiceMutation.mutateAsync(transactionData);
      
    } catch (error) {
      console.error('Error creating purchase invoice:', error);
      setError('Failed to create purchase invoice. Please try again.');
    }
  };

  // Helper function to get main currency symbol (default currency)
  const getMainCurrencySymbol = () => {
    if (!Array.isArray(currencies) || currencies.length === 0) {
      return '$';
    }
    const defaultCurrency = currencies.find((c: any) => c.is_default);
    return defaultCurrency?.symbol || '$';
  };

  const getCurrencySymbol = () => {
    // First, try to find the property's currency by currency_id
    if (property?.currency_id && Array.isArray(currencies) && currencies.length > 0) {
      const propertyCurrency = currencies.find((c: any) => c.id === property.currency_id);
      if (propertyCurrency) {
        return propertyCurrency.symbol;
      }
    }
    
    // Fallback to property.currency.symbol (if it exists) or main currency
    return property?.currency?.symbol || getMainCurrencySymbol();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt />
            <Typography variant="h6">
              {t('purchase_invoice')}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {property?.name}{unit && ` - ${getUnitHierarchyNameById(unit.id)}`}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {/* Invoice Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            {t('invoice_information')}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('invoice_number')}
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DatePicker
                label={t('invoice_date')}
                value={formData.invoiceDate}
                onChange={(date) => setFormData({ ...formData, invoiceDate: date || new Date() })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <TextField
                  label={t('vendor_name')}
                  value={formData.vendorName}
                  onChange={(e) => {
                    setFormData({ ...formData, vendorName: e.target.value });
                    searchVendors(e.target.value);
                  }}
                  onFocus={() => {
                    if (formData.vendorName.length >= 2) {
                      searchVendors(formData.vendorName);
                    }
                  }}
                  onBlur={() => {
                    // Delay hiding dropdown to allow clicking on items
                    setTimeout(() => setShowVendorDropdown(false), 200);
                  }}
                  InputProps={{
                    endAdornment: vendorSearchLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => createVendor()}
                        disabled={!formData.vendorName.trim()}
                      >
                        <Add />
                      </IconButton>
                    ),
                  }}
                />
                {showVendorDropdown && vendorSearchResults.length > 0 && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: 200,
                      overflow: 'auto',
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    {vendorSearchResults.map((vendor) => (
                      <MenuItem
                        key={vendor.id}
                        onClick={() => selectVendor(vendor)}
                        sx={{ py: 1 }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {vendor.name}
                          </Typography>
                          {vendor.contact_person && (
                            <Typography variant="caption" color="textSecondary">
                              Contact: {vendor.contact_person}
                            </Typography>
                          )}
                          {vendor.phone && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              Phone: {vendor.phone}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Paper>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.vendorContactPerson}
                onChange={(e) => setFormData({ ...formData, vendorContactPerson: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.vendorEmail}
                onChange={(e) => setFormData({ ...formData, vendorEmail: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.vendorPhone}
                onChange={(e) => setFormData({ ...formData, vendorPhone: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="WhatsApp"
                value={formData.vendorWhatsapp}
                onChange={(e) => setFormData({ ...formData, vendorWhatsapp: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
                  label="Payment Method"
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DatePicker
                label={t('due_date')}
                value={formData.dueDate}
                onChange={(date) => setFormData({ ...formData, dueDate: date || new Date() })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('vendor_address')}
                value={formData.vendorAddress}
                onChange={(e) => setFormData({ ...formData, vendorAddress: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="maintenance">Maintenance & Repairs</MenuItem>
                  <MenuItem value="utilities">Utilities</MenuItem>
                  <MenuItem value="insurance">Insurance</MenuItem>
                  <MenuItem value="taxes">Taxes & Fees</MenuItem>
                  <MenuItem value="cleaning">Cleaning Services</MenuItem>
                  <MenuItem value="landscaping">Landscaping</MenuItem>
                  <MenuItem value="security">Security Services</MenuItem>
                  <MenuItem value="supplies">Supplies & Materials</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Line Items */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {t('line_items')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={addLineItem}
              size="small"
            >
              {t('add_item')}
            </Button>
          </Box>
          
          {lineItems.map((item, index) => (
            <Box key={index} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Description *"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    required
                  />
                </Grid>
                
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label="Quantity *"
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label="Unit Price *"
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => updateLineItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth required>
                    <InputLabel>Expense Category *</InputLabel>
                    <Select
                      value={item.accountId}
                      label="Expense Category *"
                      onChange={(e) => updateLineItem(index, 'accountId', e.target.value)}
                    >
                      {expenseAccounts.map((account) => (
                        <MenuItem key={account.id} value={account.id.toString()}>
                          {account.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={10} md={1}>
                  <Typography variant="body2" color="textSecondary">
                    {t('amount')}: {getCurrencySymbol()} {item.amount.toFixed(2)}
                  </Typography>
                </Grid>
                
                <Grid item xs={2} md={1}>
                  <IconButton
                    color="error"
                    onClick={() => removeLineItem(index)}
                    disabled={lineItems.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Typography variant="h6" color="primary">
              {t('total_amount')}: {getCurrencySymbol()} {getTotalAmount().toFixed(2)}
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label={t('additional_notes_optional')}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={loading || createPurchaseInvoiceMutation.isPending}
          >
            {loading || createPurchaseInvoiceMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('create_invoice')
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for expense category warning */}
      <Snackbar
        open={showCategoryTooltip}
        autoHideDuration={3000}
        onClose={() => setShowCategoryTooltip(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="warning" 
          onClose={() => setShowCategoryTooltip(false)}
          sx={{ width: '100%' }}
        >
          {t('please_select_expense_category_first')}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default PurchaseInvoiceDialog; 